/**
 * StruckPrice — a price with what it would have cost struck through beside it.
 *
 * Renders nothing but the charged price when there is no saving, so every call site can hand it
 * both figures unconditionally and never branch.
 *
 * There is deliberately **no comparison here**. The backend only sends a `basePrice` when it is
 * strictly greater than `price` and in the same currency (`strikePrice`), so a channel MARK-UP
 * cannot arrive and render as a discount — and a second comparison on the client is a second place
 * for the rule to drift. Legacy compared `Big` values at each of its own call sites for exactly the
 * reason this contract now removes.
 *
 * @example
 * <StruckPrice price="129,00 €" base="149,00 €" />
 */

import React from 'react'
import { Stack, Typography } from '@mui/material'

import { GRAY_700, GRAY_900, GREEN_600 } from 'constants/styling/theme'

interface Props {
  /** The figure the client will be charged, already formatted. */
  price: string
  /** The figure without the saving, already formatted. Absent = nothing to strike. */
  base?: string | null
  /** Typography variant of the charged price; the struck one always renders a step down. */
  variant?: 'text-sm' | 'text-md'
  /** Screen-reader label for the struck figure. Defaults to the shared "previous price" copy. */
  baseAriaLabel?: string
  /** Shows the charged price green: the OneSystem quota makes it free. */
  free?: boolean
}

export const StruckPrice: React.FC<Props> = ({ price, base, variant = 'text-md', baseAriaLabel, free = false }) => {
  if (!base) return <>{price}</>

  return (
    <Stack direction="row" alignItems="baseline" gap={0.6} component="span" data-testid="pf-struck-price">
      {/* Order matters for a screen reader: the price that will be charged is read first, and the
          struck one follows as context rather than being mistaken for it. */}
      <Typography variant={variant} color={free ? GREEN_600 : GRAY_900} component="span">
        {price}
      </Typography>
      <Typography
        variant="text-sm"
        color={GRAY_700}
        component="span"
        aria-label={baseAriaLabel}
        data-testid="pf-struck-price-base"
        sx={{ textDecoration: 'line-through' }}
      >
        {base}
      </Typography>
    </Stack>
  )
}
